import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import LineItemsTable from './LineItemsTable';
import { useAuth } from '../../../contexts/AuthContext';


const SalesFormModal = ({ isOpen, onClose, type, item, onSave, customerOptions }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Get organization ID from auth context
  const organizationId = userProfile?.organization_id;

  // Load items for line item selection
  const loadItems = async () => {
    if (!isOpen || !['invoices', 'quotes', 'credit-notes']?.includes(type)) {
      setItemOptions([]);
      return;
    }

    setItemsLoading(true);
    try {
      // Show loading state immediately
      setItemOptions([]);
      
      // Check if organizationId is available
      if (!organizationId) {
        console.warn('Organization ID not available for loading items. User may not be authenticated.');
        // Set empty options with helpful message
        setItemOptions([{
          value: '',
          label: 'Please log in to load items from inventory',
          item: null,
          disabled: true
        }]);
        setItemsLoading(false);
        return;
      }

      // Import items service
      const { itemsManagementService } = await import('../../../services/itemsManagementService');
      
      const result = await itemsManagementService?.getItems(organizationId, { is_active: true });
      
      if (result?.error) {
        console.error('Error loading items:', result?.error);
        setItemOptions([{
          value: '',
          label: 'Error loading items from inventory',
          item: null,
          disabled: true
        }]);
      } else if (!result?.data || result?.data?.length === 0) {
        // No items found
        setItemOptions([{
          value: '',
          label: 'No items found in inventory. Please add items in Item Management.',
          item: null,
          disabled: true
        }]);
      } else {
        // Transform items for selection
        const options = result?.data?.map(item => ({
          value: item?.id,
          label: `${item?.item_name || item?.name} - ${item?.item_number || item?.sku}`,
          item: {
            id: item?.id,
            item_name: item?.item_name || item?.name,
            item_number: item?.item_number || item?.sku,
            selling_price: item?.selling_price || item?.price || 0,
            tax_rate: item?.tax_rate || item?.taxRate || 0,
            weight_per_unit: item?.weight_per_unit || item?.weight || 1,
            unit_of_measure: item?.unit_of_measure || item?.unit || 'each',
            description: item?.description || '',
            category: item?.category || ''
          }
        }));
        setItemOptions(options);
      }
    } catch (error) {
      console.error('Error loading items:', error);
      setItemOptions([{
        value: '',
        label: 'Failed to load items. Please try again.',
        item: null,
        disabled: true
      }]);
    } finally {
      setItemsLoading(false);
    }
  };

  // Initialize form data based on type and item
  useEffect(() => {
    if (!isOpen) return;

    const initializeFormData = () => {
      if (item) {
        // Editing existing item
        const data = { ...item };
        
        // CRITICAL FIX: Load existing line items if editing invoice/quote/credit-notes
        if (['invoices', 'quotes', 'credit-notes']?.includes(type) && item?.line_items) {
          // Ensure line items have proper structure and line numbers
          const formattedLineItems = item?.line_items?.map((lineItem, index) => ({
            ...lineItem,
            line_number: lineItem?.line_number || (index + 1),
            id: lineItem?.id || Date.now() + index // Ensure each line item has an ID
          }));
          setLineItems(formattedLineItems);
        } else if (['invoices', 'quotes', 'credit-notes']?.includes(type)) {
          // If no line items exist but this is an invoice/quote/credit-note, initialize empty array
          setLineItems([]);
        }
        
        return data;
      }

      // Creating new item - set defaults based on type
      switch (type) {
        case 'customers':
          return {
            customer_name: '',
            email: '',
            phone: '',
            company_name: '',
            website: '',
            payment_terms: 'net_30',
            currency_code: 'USD',
            credit_limit: 0,
            status: 'active',
            notes: ''
          };
        case 'invoices':
          return {
            customer_id: '',
            issue_date: new Date()?.toISOString()?.split('T')?.[0],
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
            subtotal: 0,
            tax_amount: 0,
            total_amount: 0,
            payment_terms: 'net_30',
            currency_code: 'USD',
            status: 'draft',
            notes: '',
            tax_type: 'tax_exclusive',
            tax_level: 'line_item_level'
          };
        case 'quotes':
          return {
            customer_id: '',
            issue_date: new Date()?.toISOString()?.split('T')?.[0],
            valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
            subtotal: 0,
            tax_amount: 0,
            total_amount: 0,
            currency_code: 'USD',
            status: 'draft',
            notes: '',
            terms_and_conditions: '',
            tax_type: 'tax_exclusive',
            tax_level: 'line_item_level'
          };
        case 'retainer-invoices':
          return {
            customer_id: '',
            retainer_amount: 0,
            start_date: new Date()?.toISOString()?.split('T')?.[0],
            end_date: '',
            billing_cycle: 'monthly',
            currency_code: 'USD',
            status: 'draft',
            description: '',
            usage_guidelines: '',
            terms_and_conditions: ''
          };
        case 'recurring-invoices':
          return {
            customer_id: '',
            template_name: '',
            description: '',
            start_date: new Date()?.toISOString()?.split('T')?.[0],
            end_date: '',
            frequency: 'monthly',
            total_cycles: '',
            currency_code: 'USD',
            status: 'draft',
            invoice_template: {},
            notes: ''
          };
        case 'delivery-challans':
          return {
            customer_id: '',
            delivery_address: '',
            expected_delivery_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
            transport_mode: 'road',
            transporter_name: '',
            vehicle_number: '',
            driver_name: '',
            driver_phone: '',
            status: 'draft',
            delivery_instructions: '',
            notes: ''
          };
        case 'payment-links':
          return {
            customer_id: '',
            description: '',
            amount: 0,
            currency_code: 'USD',
            expiry_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
            allow_partial_payment: false,
            minimum_payment_amount: 0,
            custom_message: '',
            status: 'draft'
          };
        case 'payments-received':
          return {
            customer_id: '',
            amount_received: 0,
            payment_method: 'bank_transfer',
            payment_date: new Date()?.toISOString()?.split('T')?.[0],
            reference_number: '',
            bank_name: '',
            account_reference: '',
            description: '',
            notes: '',
            status: 'pending',
            currency_code: 'USD',
            reference_invoice_id: null,
            reference_payment_link_id: null
          };
        case 'credit-notes':
          return {
            customer_id: '',
            reference_invoice_id: null,
            credit_reason: 'product_return',
            total_credit_amount: 0,
            subtotal: 0,
            tax_amount: 0,
            currency_code: 'USD',
            status: 'draft',
            description: '',
            reason_description: '',
            notes: '',
            tax_type: 'tax_exclusive',
            tax_level: 'line_item_level'
          };
        default:
          return {};
      }
    };

    const initialData = initializeFormData();
    setFormData(initialData);
    setErrors({});
    
    // CRITICAL FIX: Only reset line items for new items (not editing existing items)
    if (!item) {
      // Only reset for new items that don't require line items
      if (!['invoices', 'quotes', 'credit-notes']?.includes(type)) {
        setLineItems([]);
      } else {
        // For new invoices/quotes/credit-notes, start with empty line items (will be auto-populated)
        setLineItems([]);
      }
    }
  }, [isOpen, type, item]);

  // Load items when form opens for invoice/quote types
  useEffect(() => {
    loadItems();
  }, [isOpen, type, organizationId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Auto-calculate totals for financial forms (when not using line items)
    if (field === 'subtotal' || field === 'tax_amount') {
      const subtotal = parseFloat(field === 'subtotal' ? value : formData?.subtotal || 0);
      const taxAmount = parseFloat(field === 'tax_amount' ? value : formData?.tax_amount || 0);
      
      setFormData(prev => ({
        ...prev,
        [field]: value,
        total_amount: subtotal + taxAmount
      }));
    }
  };

  // Handle line items change and update form totals
  const handleLineItemsChange = (items) => {
    setLineItems(items);
    
    // Calculate totals from line items
    const subtotal = items?.reduce((sum, item) => sum + (parseFloat(item?.line_total) || 0), 0);
    const taxAmount = items?.reduce((sum, item) => sum + (parseFloat(item?.tax_amount) || 0), 0);
    const totalAmount = items?.reduce((sum, item) => sum + (parseFloat(item?.line_total_with_tax) || 0), 0);
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total_amount: totalAmount
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Common validations
    switch (type) {
      case 'customers':
        if (!formData?.customer_name?.trim()) {
          newErrors.customer_name = 'Customer name is required';
        }
        if (!formData?.email?.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        break;

      case 'invoices': case'quotes':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        
        // Validate line items for invoices and quotes
        if (lineItems?.length === 0) {
          newErrors.line_items = 'At least one line item is required';
        } else {
          // Validate each line item
          let hasLineItemErrors = false;
          lineItems?.forEach((item, index) => {
            if (!item?.item_description?.trim()) {
              newErrors[`line_item_${index}_description`] = 'Item description is required';
              hasLineItemErrors = true;
            }
            if (!item?.quantity || parseFloat(item?.quantity) <= 0) {
              newErrors[`line_item_${index}_quantity`] = 'Quantity must be greater than 0';
              hasLineItemErrors = true;
            }
            if (!item?.unit_price || parseFloat(item?.unit_price) < 0) {
              newErrors[`line_item_${index}_unit_price`] = 'Unit price must be 0 or greater';
              hasLineItemErrors = true;
            }
          });
          
          if (hasLineItemErrors) {
            newErrors.line_items = 'Please fix line item errors';
          }
        }
        
        if (!formData?.total_amount || parseFloat(formData?.total_amount) <= 0) {
          newErrors.total_amount = 'Total amount must be greater than 0';
        }
        break;

      case 'retainer-invoices':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        if (!formData?.retainer_amount || parseFloat(formData?.retainer_amount) <= 0) {
          newErrors.retainer_amount = 'Retainer amount must be greater than 0';
        }
        if (!formData?.start_date) {
          newErrors.start_date = 'Start date is required';
        }
        break;

      case 'recurring-invoices':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        if (!formData?.template_name?.trim()) {
          newErrors.template_name = 'Template name is required';
        }
        if (!formData?.start_date) {
          newErrors.start_date = 'Start date is required';
        }
        break;

      case 'delivery-challans':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        if (!formData?.delivery_address?.trim()) {
          newErrors.delivery_address = 'Delivery address is required';
        }
        if (!formData?.expected_delivery_date) {
          newErrors.expected_delivery_date = 'Expected delivery date is required';
        }
        break;

      case 'payment-links':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        if (!formData?.amount || parseFloat(formData?.amount) <= 0) {
          newErrors.amount = 'Amount must be greater than 0';
        }
        if (!formData?.description?.trim()) {
          newErrors.description = 'Description is required';
        }
        break;

      case 'payments-received':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        if (!formData?.amount_received || parseFloat(formData?.amount_received) <= 0) {
          newErrors.amount_received = 'Amount received must be greater than 0';
        }
        if (!formData?.payment_method) {
          newErrors.payment_method = 'Payment method is required';
        }
        if (!formData?.payment_date) {
          newErrors.payment_date = 'Payment date is required';
        }
        break;

      case 'credit-notes':
        if (!formData?.customer_id) {
          newErrors.customer_id = 'Customer is required';
        }
        
        // Validate line items for credit notes
        if (lineItems?.length === 0) {
          newErrors.line_items = 'At least one line item is required';
        }
        
        if (!formData?.total_credit_amount || parseFloat(formData?.total_credit_amount) <= 0) {
          newErrors.total_credit_amount = 'Credit amount must be greater than 0';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare data for submission including line items
      const submissionData = {
        ...formData,
        line_items: ['invoices', 'quotes', 'credit-notes']?.includes(type) ? lineItems : undefined
      };
      
      await onSave(submissionData);
      // Success feedback - the modal will be closed by parent component
      console.log('Form submitted successfully');
    } catch (error) {
      console.error('Error saving:', error);
      // Error is handled by parent component
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    const action = item ? 'Edit' : 'Create New';
    switch (type) {
      case 'customers': return `${action} Customer`;
      case 'invoices': return `${action} Invoice`;
      case 'quotes': return `${action} Quote`;
      case 'retainer-invoices': return `${action} Retainer Invoice`;
      case 'recurring-invoices': return `${action} Recurring Invoice`;
      case 'delivery-challans': return `${action} Delivery Challan`;
      case 'payment-links': return `${action} Payment Link`;
      case 'payments-received': return `${action} Payment Received`;
      case 'credit-notes': return `${action} Credit Note`;
      default: return `${action} Item`;
    }
  };

  const renderFormFields = () => {
    switch (type) {
      case 'customers':
        return (
          <div className="space-y-8">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Customer Name *"
                    value={formData?.customer_name || ''}
                    onChange={(e) => handleInputChange('customer_name', e?.target?.value)}
                    error={errors?.customer_name}
                    placeholder="Enter customer name"
                  />
                </div>
                <div>
                  <Input
                    label="Email *"
                    type="email"
                    value={formData?.email || ''}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    placeholder="customer@example.com"
                  />
                </div>
                <div>
                  <Input
                    label="Phone"
                    value={formData?.phone || ''}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Input
                    label="Company Name"
                    value={formData?.company_name || ''}
                    onChange={(e) => handleInputChange('company_name', e?.target?.value)}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <Input
                    label="Website"
                    value={formData?.website || ''}
                    onChange={(e) => handleInputChange('website', e?.target?.value)}
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <Select
                    label="Status"
                    value={formData?.status || 'active'}
                    onChange={(value) => handleInputChange('status', value)}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                      { value: 'suspended', label: 'Suspended' },
                      { value: 'archived', label: 'Archived' }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Payment & Billing */}
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Payment & Billing
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Select
                    label="Payment Terms"
                    value={formData?.payment_terms || 'net_30'}
                    onChange={(value) => handleInputChange('payment_terms', value)}
                    options={[
                      { value: 'advance_payment', label: 'Advance Payment' },
                      { value: 'net_15', label: 'Net 15' },
                      { value: 'net_30', label: 'Net 30' },
                      { value: 'net_45', label: 'Net 45' },
                      { value: 'net_60', label: 'Net 60' },
                      { value: 'due_on_receipt', label: 'Due on Receipt' },
                      { value: 'cash_on_delivery', label: 'Cash on Delivery' }
                    ]}
                  />
                </div>
                <div>
                  <Input
                    label="Credit Limit"
                    type="number"
                    value={formData?.credit_limit || 0}
                    onChange={(e) => handleInputChange('credit_limit', e?.target?.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes || ''}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Any additional notes about this customer..."
              />
            </div>
          </div>
        );

      case 'invoices':
        return (
          <div className="space-y-8">
            {/* Invoice Header */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Invoice Details
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <Select
                    label="Customer *"
                    value={formData?.customer_id || ''}
                    onChange={(value) => handleInputChange('customer_id', value)}
                    options={customerOptions || []}
                    error={errors?.customer_id}
                    placeholder={customerOptions?.length > 0 ? "Select a customer" : "No customers available"}
                  />
                  {(!customerOptions || customerOptions?.length === 0) && (
                    <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                      <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      No customers found. Please create a customer first.
                    </p>
                  )}
                </div>
                <div>
                  <Input
                    label="Issue Date *"
                    type="date"
                    value={formData?.issue_date || ''}
                    onChange={(e) => handleInputChange('issue_date', e?.target?.value)}
                  />
                </div>
                <div>
                  <Input
                    label="Due Date *"
                    type="date"
                    value={formData?.due_date || ''}
                    onChange={(e) => handleInputChange('due_date', e?.target?.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div>
                  <Select
                    label="Payment Terms"
                    value={formData?.payment_terms || 'net_30'}
                    onChange={(value) => handleInputChange('payment_terms', value)}
                    options={[
                      { value: 'advance_payment', label: 'Advance Payment' },
                      { value: 'net_15', label: 'Net 15' },
                      { value: 'net_30', label: 'Net 30' },
                      { value: 'net_45', label: 'Net 45' },
                      { value: 'net_60', label: 'Net 60' },
                      { value: 'due_on_receipt', label: 'Due on Receipt' },
                      { value: 'cash_on_delivery', label: 'Cash on Delivery' }
                    ]}
                  />
                </div>
                <div>
                  <Select
                    label="Status"
                    value={formData?.status || 'draft'}
                    onChange={(value) => handleInputChange('status', value)}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'sent', label: 'Sent' },
                      { value: 'viewed', label: 'Viewed' },
                      { value: 'paid', label: 'Paid' },
                      { value: 'partially_paid', label: 'Partially Paid' },
                      { value: 'overdue', label: 'Overdue' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Tax Configuration */}
            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Tax Configuration
              </h4>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <Select
                    label="Tax Configuration"
                    value={`${formData?.tax_type || 'tax_exclusive'}_${formData?.tax_level || 'line_item_level'}`}
                    onChange={(value) => {
                      const [taxType, taxLevel] = value?.split('_');
                      handleInputChange('tax_type', taxType);
                      handleInputChange('tax_level', taxLevel === 'line' ? 'line_item_level' : 'transaction_level');
                    }}
                    options={[
                      { value: 'tax_exclusive_line_item_level', label: 'Tax Exclusive - Line Item Level' },
                      { value: 'tax_exclusive_transaction_level', label: 'Tax Exclusive - Transaction Level' },
                      { value: 'tax_inclusive_line_item_level', label: 'Tax Inclusive - Line Item Level' },
                      { value: 'tax_inclusive_transaction_level', label: 'Tax Inclusive - Transaction Level' }
                    ]}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Choose how taxes are calculated and applied to this invoice.
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items Section - CRITICAL FIX */}
            <div className="bg-gray-50 rounded-lg p-6">
              <LineItemsTable
                items={lineItems}
                onChange={handleLineItemsChange}
                productItems={itemOptions}
                taxSettings={{
                  taxType: formData?.tax_type || 'tax_exclusive',
                  taxLevel: formData?.tax_level || 'line_item_level'
                }}
                currency={formData?.currency_code || 'USD'}
                itemsLoading={itemsLoading}
               
              />
              {errors?.line_items && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors?.line_items}
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Notes
              </label>
              <textarea
                value={formData?.notes || ''}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Additional notes that will appear on the invoice..."
              />
            </div>
          </div>
        );

      case 'quotes':
        return (
          <div className="space-y-8">
            {/* Quote Header */}
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Quote Details
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Select
                    label="Customer *"
                    value={formData?.customer_id || ''}
                    onChange={(value) => handleInputChange('customer_id', value)}
                    options={customerOptions || []}
                    error={errors?.customer_id}
                    placeholder={customerOptions?.length > 0 ? "Select a customer" : "No customers available"}
                  />
                  {(!customerOptions || customerOptions?.length === 0) && (
                    <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                      No customers found. Please create a customer first.
                    </p>
                  )}
                </div>
                <div>
                  <Select
                    label="Status"
                    value={formData?.status || 'draft'}
                    onChange={(value) => handleInputChange('status', value)}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'sent', label: 'Sent' },
                      { value: 'accepted', label: 'Accepted' },
                      { value: 'declined', label: 'Declined' },
                      { value: 'expired', label: 'Expired' }
                    ]}
                  />
                </div>
                <div>
                  <Input
                    label="Issue Date *"
                    type="date"
                    value={formData?.issue_date || ''}
                    onChange={(e) => handleInputChange('issue_date', e?.target?.value)}
                  />
                </div>
                <div>
                  <Input
                    label="Valid Until *"
                    type="date"
                    value={formData?.valid_until || ''}
                    onChange={(e) => handleInputChange('valid_until', e?.target?.value)}
                  />
                </div>
                <div>
                  <Select
                    label="Validity Period"
                    value={formData?.validity_period || 'days_30'}
                    onChange={(value) => handleInputChange('validity_period', value)}
                    options={[
                      { value: 'days_7', label: '7 Days' },
                      { value: 'days_15', label: '15 Days' },
                      { value: 'days_30', label: '30 Days' },
                      { value: 'days_45', label: '45 Days' },
                      { value: 'days_60', label: '60 Days' },
                      { value: 'days_90', label: '90 Days' }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Tax Configuration */}
            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Tax Configuration
              </h4>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <Select
                    label="Tax Configuration"
                    value={`${formData?.tax_type || 'tax_exclusive'}_${formData?.tax_level || 'line_item_level'}`}
                    onChange={(value) => {
                      const [taxType, taxLevel] = value?.split('_', 2);
                      handleInputChange('tax_type', taxType);
                      handleInputChange('tax_level', taxLevel === 'line' ? 'line_item_level' : 'transaction_level');
                    }}
                    options={[
                      { value: 'tax_exclusive_line_item_level', label: 'Tax Exclusive - Line Item Level' },
                      { value: 'tax_exclusive_transaction_level', label: 'Tax Exclusive - Transaction Level' },
                      { value: 'tax_inclusive_line_item_level', label: 'Tax Inclusive - Line Item Level' },
                      { value: 'tax_inclusive_transaction_level', label: 'Tax Inclusive - Transaction Level' }
                    ]}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Choose how taxes are calculated and applied to this quote.
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <LineItemsTable
                items={lineItems}
                onChange={handleLineItemsChange}
                productItems={itemOptions}
                taxSettings={{
                  taxType: formData?.tax_type || 'tax_exclusive',
                  taxLevel: formData?.tax_level || 'line_item_level'
                }}
                currency={formData?.currency_code || 'USD'}
               
              />
              {errors?.line_items && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors?.line_items}
                  </p>
                </div>
              )}
            </div>

            {/* Terms and Conditions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms and Conditions
              </label>
              <textarea
                value={formData?.terms_and_conditions || ''}
                onChange={(e) => handleInputChange('terms_and_conditions', e?.target?.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Quote terms and conditions..."
              />
            </div>
            
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes || ''}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Additional notes..."
              />
            </div>
          </div>
        );

      case 'retainer-invoices':
        return (
          <div className="space-y-8">
            {/* Retainer Details */}
            <div className="bg-indigo-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 002 2z" />
                </svg>
                Retainer Details
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Select
                    label="Customer *"
                    value={formData?.customer_id || ''}
                    onChange={(value) => handleInputChange('customer_id', value)}
                    options={customerOptions}
                    error={errors?.customer_id}
                    placeholder="Select a customer"
                  />
                </div>
                <div>
                  <Input
                    label="Retainer Amount *"
                    type="number"
                    step="0.01"
                    value={formData?.retainer_amount || 0}
                    onChange={(e) => handleInputChange('retainer_amount', e?.target?.value)}
                    error={errors?.retainer_amount}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Input
                    label="Start Date *"
                    type="date"
                    value={formData?.start_date || ''}
                    onChange={(e) => handleInputChange('start_date', e?.target?.value)}
                    error={errors?.start_date}
                  />
                </div>
                <div>
                  <Input
                    label="End Date"
                    type="date"
                    value={formData?.end_date || ''}
                    onChange={(e) => handleInputChange('end_date', e?.target?.value)}
                  />
                </div>
                <div>
                  <Select
                    label="Billing Cycle"
                    value={formData?.billing_cycle || 'monthly'}
                    onChange={(value) => handleInputChange('billing_cycle', value)}
                    options={[
                      { value: 'monthly', label: 'Monthly' },
                      { value: 'quarterly', label: 'Quarterly' },
                      { value: 'half_yearly', label: 'Half Yearly' },
                      { value: 'yearly', label: 'Yearly' }
                    ]}
                  />
                </div>
                <div>
                  <Select
                    label="Status"
                    value={formData?.status || 'draft'}
                    onChange={(value) => handleInputChange('status', value)}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'active', label: 'Active' },
                      { value: 'partially_utilized', label: 'Partially Utilized' },
                      { value: 'fully_utilized', label: 'Fully Utilized' },
                      { value: 'expired', label: 'Expired' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                  />
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Description
              </label>
              <textarea
                value={formData?.description || ''}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Describe the retainer services..."
              />
            </div>
          </div>
        );

      case 'recurring-invoices':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Customer *"
                  value={formData?.customer_id || ''}
                  onChange={(value) => handleInputChange('customer_id', value)}
                  options={customerOptions}
                  error={errors?.customer_id}
                  placeholder="Select a customer"
                />
              </div>
              <div>
                <Input
                  label="Template Name *"
                  value={formData?.template_name || ''}
                  onChange={(e) => handleInputChange('template_name', e?.target?.value)}
                  error={errors?.template_name}
                  placeholder="Monthly Service Invoice"
                />
              </div>
              <div>
                <Input
                  label="Start Date *"
                  type="date"
                  value={formData?.start_date || ''}
                  onChange={(e) => handleInputChange('start_date', e?.target?.value)}
                  error={errors?.start_date}
                />
              </div>
              <div>
                <Input
                  label="End Date"
                  type="date"
                  value={formData?.end_date || ''}
                  onChange={(e) => handleInputChange('end_date', e?.target?.value)}
                />
              </div>
              <div>
                <Select
                  label="Frequency"
                  value={formData?.frequency || 'monthly'}
                  onChange={(value) => handleInputChange('frequency', value)}
                  options={[
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'yearly', label: 'Yearly' }
                  ]}
                />
              </div>
              <div>
                <Input
                  label="Total Cycles"
                  type="number"
                  value={formData?.total_cycles || ''}
                  onChange={(e) => handleInputChange('total_cycles', e?.target?.value)}
                  placeholder="Leave empty for unlimited"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData?.description || ''}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the recurring invoice"
              />
            </div>
          </>
        );

      case 'delivery-challans':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Customer *"
                  value={formData?.customer_id || ''}
                  onChange={(value) => handleInputChange('customer_id', value)}
                  options={customerOptions}
                  error={errors?.customer_id}
                  placeholder="Select a customer"
                />
              </div>
              <div>
                <Input
                  label="Expected Delivery Date *"
                  type="date"
                  value={formData?.expected_delivery_date || ''}
                  onChange={(e) => handleInputChange('expected_delivery_date', e?.target?.value)}
                  error={errors?.expected_delivery_date}
                />
              </div>
              <div>
                <Select
                  label="Transport Mode"
                  value={formData?.transport_mode || 'road'}
                  onChange={(value) => handleInputChange('transport_mode', value)}
                  options={[
                    { value: 'road', label: 'Road' },
                    { value: 'rail', label: 'Rail' },
                    { value: 'air', label: 'Air' },
                    { value: 'sea', label: 'Sea' },
                    { value: 'courier', label: 'Courier' },
                    { value: 'pickup', label: 'Pickup' }
                  ]}
                />
              </div>
              <div>
                <Input
                  label="Transporter Name"
                  value={formData?.transporter_name || ''}
                  onChange={(e) => handleInputChange('transporter_name', e?.target?.value)}
                  placeholder="Transporter company name"
                />
              </div>
              <div>
                <Input
                  label="Vehicle Number"
                  value={formData?.vehicle_number || ''}
                  onChange={(e) => handleInputChange('vehicle_number', e?.target?.value)}
                  placeholder="Vehicle registration number"
                />
              </div>
              <div>
                <Input
                  label="Driver Name"
                  value={formData?.driver_name || ''}
                  onChange={(e) => handleInputChange('driver_name', e?.target?.value)}
                  placeholder="Driver's name"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Address *
              </label>
              <textarea
                value={formData?.delivery_address || ''}
                onChange={(e) => handleInputChange('delivery_address', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Complete delivery address"
              />
              {errors?.delivery_address && (
                <p className="text-sm text-red-600 mt-1">{errors?.delivery_address}</p>
              )}
            </div>
          </>
        );

      case 'payment-links':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Customer *"
                  value={formData?.customer_id || ''}
                  onChange={(value) => handleInputChange('customer_id', value)}
                  options={customerOptions}
                  error={errors?.customer_id}
                  placeholder="Select a customer"
                />
              </div>
              <div>
                <Input
                  label="Amount *"
                  type="number"
                  step="0.01"
                  value={formData?.amount || 0}
                  onChange={(e) => handleInputChange('amount', e?.target?.value)}
                  error={errors?.amount}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Input
                  label="Expiry Date"
                  type="date"
                  value={formData?.expiry_date || ''}
                  onChange={(e) => handleInputChange('expiry_date', e?.target?.value)}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allow_partial_payment"
                  checked={formData?.allow_partial_payment || false}
                  onChange={(e) => handleInputChange('allow_partial_payment', e?.target?.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="allow_partial_payment" className="ml-2 block text-sm text-gray-900">
                  Allow Partial Payment
                </label>
              </div>
              {formData?.allow_partial_payment && (
                <div>
                  <Input
                    label="Minimum Payment Amount"
                    type="number"
                    step="0.01"
                    value={formData?.minimum_payment_amount || 0}
                    onChange={(e) => handleInputChange('minimum_payment_amount', e?.target?.value)}
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>
            <div>
              <Input
                label="Description *"
                value={formData?.description || ''}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                error={errors?.description}
                placeholder="Payment description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Message
              </label>
              <textarea
                value={formData?.custom_message || ''}
                onChange={(e) => handleInputChange('custom_message', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Custom message for payment link"
              />
            </div>
          </>
        );

      case 'payments-received':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Customer *"
                  value={formData?.customer_id || ''}
                  onChange={(value) => handleInputChange('customer_id', value)}
                  options={customerOptions || []}
                  error={errors?.customer_id}
                  placeholder={customerOptions?.length > 0 ? "Select a customer" : "No customers available"}
                />
                {(!customerOptions || customerOptions?.length === 0) && (
                  <p className="text-xs text-amber-600 mt-1">
                    No customers found. Please create a customer first.
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Amount Received *"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData?.amount_received || 0}
                  onChange={(e) => handleInputChange('amount_received', e?.target?.value)}
                  error={errors?.amount_received}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Select
                  label="Payment Method *"
                  value={formData?.payment_method || 'bank_transfer'}
                  onChange={(value) => handleInputChange('payment_method', value)}
                  options={[
                    { value: 'cash', label: 'Cash' },
                    { value: 'check', label: 'Check' },
                    { value: 'bank_transfer', label: 'Bank Transfer' },
                    { value: 'credit_card', label: 'Credit Card' },
                    { value: 'debit_card', label: 'Debit Card' },
                    { value: 'online', label: 'Online Payment' },
                    { value: 'upi', label: 'UPI' },
                    { value: 'wallet', label: 'Digital Wallet' }
                  ]}
                  error={errors?.payment_method}
                />
              </div>
              <div>
                <Input
                  label="Payment Date *"
                  type="date"
                  value={formData?.payment_date || ''}
                  onChange={(e) => handleInputChange('payment_date', e?.target?.value)}
                  error={errors?.payment_date}
                />
              </div>
              <div>
                <Input
                  label="Reference Number"
                  value={formData?.reference_number || ''}
                  onChange={(e) => handleInputChange('reference_number', e?.target?.value)}
                  placeholder="Transaction/Check number"
                />
              </div>
              <div>
                <Select
                  label="Status"
                  value={formData?.status || 'pending'}
                  onChange={(value) => handleInputChange('status', value)}
                  options={[
                    { value: 'pending', label: 'Pending Clearance' },
                    { value: 'cleared', label: 'Cleared' },
                    { value: 'bounced', label: 'Bounced' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'refunded', label: 'Refunded' }
                  ]}
                />
              </div>
              
              {/* Conditional fields based on payment method */}
              {(formData?.payment_method === 'bank_transfer' || 
                formData?.payment_method === 'check') && (
                <>
                  <div>
                    <Input
                      label="Bank Name"
                      value={formData?.bank_name || ''}
                      onChange={(e) => handleInputChange('bank_name', e?.target?.value)}
                      placeholder="Bank name"
                    />
                  </div>
                  
                  <div>
                    <Input
                      label="Account Reference"
                      value={formData?.account_reference || ''}
                      onChange={(e) => handleInputChange('account_reference', e?.target?.value)}
                      placeholder="Account number or reference"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <Input
                  label="Description"
                  value={formData?.description || ''}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Payment description"
                />
              </div>

              <div>
                <Select
                  label="Currency"
                  value={formData?.currency_code || 'USD'}
                  onChange={(value) => handleInputChange('currency_code', value)}
                  options={[
                    { value: 'USD', label: 'USD - US Dollar' },
                    { value: 'EUR', label: 'EUR - Euro' },
                    { value: 'GBP', label: 'GBP - British Pound' },
                    { value: 'INR', label: 'INR - Indian Rupee' },
                    { value: 'CAD', label: 'CAD - Canadian Dollar' },
                    { value: 'AUD', label: 'AUD - Australian Dollar' }
                  ]}
                />
              </div>

              <div>
                <Select
                  label="Reference Invoice (Optional)"
                  value={formData?.reference_invoice_id || ''}
                  onChange={(value) => handleInputChange('reference_invoice_id', value)}
                  options={[
                    { value: '', label: 'No invoice reference' },
                    // Invoice options would be populated from a separate API call
                    // This is a placeholder - in real implementation, you'd fetch invoices
                  ]}
                  placeholder="Select invoice (if applicable)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes || ''}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional notes about this payment"
              />
            </div>
          </>
        );

      case 'credit-notes':
        return (
          <div className="space-y-8">
            {/* Credit Note Header */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Credit Note Details
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Select
                    label="Customer *"
                    value={formData?.customer_id || ''}
                    onChange={(value) => handleInputChange('customer_id', value)}
                    options={customerOptions || []}
                    error={errors?.customer_id}
                    placeholder={customerOptions?.length > 0 ? "Select a customer" : "No customers available"}
                  />
                  {(!customerOptions || customerOptions?.length === 0) && (
                    <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                      <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      No customers found. Please create a customer first.
                    </p>
                  )}
                </div>
                <div>
                  <Select
                    label="Credit Reason"
                    value={formData?.credit_reason || 'product_return'}
                    onChange={(value) => handleInputChange('credit_reason', value)}
                    options={[
                      { value: 'product_return', label: 'Product Return' },
                      { value: 'pricing_adjustment', label: 'Pricing Adjustment' },
                      { value: 'service_issue', label: 'Service Issue' },
                      { value: 'overpayment', label: 'Overpayment' },
                      { value: 'discount', label: 'Discount' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                </div>
                <div>
                  <Select
                    label="Reference Invoice (Optional)"
                    value={formData?.reference_invoice_id || ''}
                    onChange={(value) => handleInputChange('reference_invoice_id', value)}
                    options={[
                      { value: '', label: 'No invoice reference' },
                      // Invoice options would be populated from a separate API call in a real implementation
                    ]}
                    placeholder="Select invoice (if applicable)"
                  />
                </div>
                <div>
                  <Select
                    label="Status"
                    value={formData?.status || 'draft'}
                    onChange={(value) => handleInputChange('status', value)}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'issued', label: 'Issued' },
                      { value: 'applied', label: 'Applied' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Tax Configuration */}
            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Tax Configuration
              </h4>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <Select
                    label="Tax Configuration"
                    value={`${formData?.tax_type || 'tax_exclusive'}_${formData?.tax_level || 'line_item_level'}`}
                    onChange={(value) => {
                      const [taxType, taxLevel] = value?.split('_', 2);
                      handleInputChange('tax_type', taxType);
                      handleInputChange('tax_level', taxLevel === 'line' ? 'line_item_level' : 'transaction_level');
                    }}
                    options={[
                      { value: 'tax_exclusive_line_item_level', label: 'Tax Exclusive - Line Item Level' },
                      { value: 'tax_exclusive_transaction_level', label: 'Tax Exclusive - Transaction Level' },
                      { value: 'tax_inclusive_line_item_level', label: 'Tax Inclusive - Line Item Level' },
                      { value: 'tax_inclusive_transaction_level', label: 'Tax Inclusive - Transaction Level' }
                    ]}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Choose how taxes are calculated and applied to this credit note.
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items Section - CRITICAL FIX */}
            <div className="bg-gray-50 rounded-lg p-6">
              <LineItemsTable
                items={lineItems}
                onChange={handleLineItemsChange}
                productItems={itemOptions}
                taxSettings={{
                  taxType: formData?.tax_type || 'tax_exclusive',
                  taxLevel: formData?.tax_level || 'line_item_level'
                }}
                currency={formData?.currency_code || 'USD'}
                itemsLoading={itemsLoading}
                type="credit-note"
              />
              {errors?.line_items && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors?.line_items}
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credit Note Description
              </label>
              <textarea
                value={formData?.description || ''}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Describe the credit note..."
              />
            </div>
            
            {/* Reason Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detailed Reason for Credit
              </label>
              <textarea
                value={formData?.reason_description || ''}
                onChange={(e) => handleInputChange('reason_description', e?.target?.value)}
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Detailed explanation for issuing this credit note..."
              />
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes || ''}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Any additional notes or comments..."
              />
            </div>
          </div>
        );

      // Add default case for unsupported types
      default:
        return (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Form fields for {type} are {item ? 'ready for editing' : 'available for creation'}.
            </p>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity backdrop-blur-sm" onClick={onClose}></div>

        {/* Modal panel - enhanced responsive design */}
        <div className={`inline-block align-bottom bg-white rounded-xl px-6 pt-6 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle ${
          ['invoices', 'quotes', 'credit-notes']?.includes(type) 
            ? 'sm:max-w-7xl w-full max-h-[90vh]' :'sm:max-w-3xl sm:w-full max-h-[85vh]'
        } sm:p-8`}>
          {/* Enhanced Header */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-200">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{getModalTitle()}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {item ? 'Update the details below' : 'Fill in the required information to create'} your {type?.replace('-', ' ')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable Form Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-12rem)]">
            <form onSubmit={handleSubmit} className="space-y-8">
              {renderFormFields()}

              {/* Enhanced Footer */}
              <div className="flex items-center justify-between pt-8 border-t border-gray-200 bg-gray-50 -mx-6 px-6 py-4 rounded-b-xl mt-8">
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Fields marked with * are required
                </div>
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={loading}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={loading}
                    disabled={loading}
                    className="px-8"
                  >
                    {loading ? 'Saving...' : (item ? 'Update' : 'Create')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFormModal;